/**
 * Canonical identity for cricket teams so a favourite can be recognised across every store:
 * the curated IPL picker (`faviplteams`), the national picker (`favnationalteams`), and
 * free-form cricket teams starred from matches (`favcustomcricketteams`).
 *
 * A fixture team name (e.g. "Royal Challengers Bengaluru", "RCB") resolves to the same
 * canonical slug as the profile picker (`royal-challengers-bangalore`) via an alias table,
 * so the star fills regardless of where the team was favourited.
 */
import { slugifyCricketTeamName } from "./cricketTeamLogoUrl";
import { PROFILE_FAV_IPL_TEAM_OPTIONS } from "../components/Profile/profileFavIplTeams";
import { PROFILE_FAV_CRICKET_INT_OPTIONS } from "../components/Profile/profileFavCricketIntTeams";

export type FavTeamCategory = "ipl" | "national" | "custom";

/** Storage key each category persists to (in `accounts.favourites`). */
export const FAV_TEAM_CATEGORY_KEY: Record<FavTeamCategory, string> = {
  ipl: "faviplteams",
  national: "favnationalteams",
  custom: "favcustomcricketteams",
};

const IPL_SLUGS = new Set<string>(PROFILE_FAV_IPL_TEAM_OPTIONS.map((t) => t.slug));
/** Profile International picker slugs. */
const PROFILE_NATIONAL_SLUGS = new Set<string>(PROFILE_FAV_CRICKET_INT_OPTIONS.map((t) => t.slug));

/**
 * Extra national / associate sides recognised when starring from matches so they
 * route into `favnationalteams` (and the International section) even when they
 * are not in the curated profile picker.
 */
const EXTRA_NATIONAL_SLUGS = new Set<string>([
  "singapore",
  "usa",
  "united-states",
  "canada",
  "namibia",
  "oman",
  "hong-kong",
  "papua-new-guinea",
  "png",
  "malaysia",
  "kenya",
  "uganda",
  "nigeria",
  "tanzania",
  "jersey",
  "guernsey",
  "cayman-islands",
  "bermuda",
  "thailand",
  "japan",
  "china",
  "vanuatu",
  "fiji",
  "samoa",
  "qatar",
  "saudi-arabia",
  "kuwait",
  "bahrain",
  "maldives",
  "indonesia",
  "philippines",
  "gibraltar",
]);

const NATIONAL_SLUGS = new Set<string>([...PROFILE_NATIONAL_SLUGS, ...EXTRA_NATIONAL_SLUGS]);

/** Alternate names/abbreviations → canonical slug (for name drift the raw slug misses). */
const ALIAS_TO_SLUG: Record<string, string> = {};
function addAlias(alias: string, slug: string) {
  const key = slugifyCricketTeamName(alias);
  if (key) ALIAS_TO_SLUG[key] = slug;
}

// IPL franchises: renamings + common abbreviations.
addAlias("Royal Challengers Bengaluru", "royal-challengers-bangalore");
addAlias("RCB", "royal-challengers-bangalore");
addAlias("Kings XI Punjab", "punjab-kings");
addAlias("PBKS", "punjab-kings");
addAlias("Delhi Daredevils", "delhi-capitals");
addAlias("DC", "delhi-capitals");
addAlias("CSK", "chennai-super-kings");
addAlias("MI", "mumbai-indians");
addAlias("KKR", "kolkata-knight-riders");
addAlias("SRH", "sunrisers-hyderabad");
addAlias("RR", "rajasthan-royals");
addAlias("GT", "gujarat-titans");
addAlias("LSG", "lucknow-super-giants");
// National sides where the slug differs from the common name.
addAlias("United Arab Emirates", "uae");
addAlias("United States", "usa");
addAlias("United States of America", "usa");
addAlias("US", "usa");
addAlias("PNG", "papua-new-guinea");

/** Resolve a team name (or a stored slug) to its canonical slug + category. */
export function resolveCanonicalTeam(value: string): {
  slug: string;
  category: FavTeamCategory;
  name: string;
} {
  const raw = String(value ?? "").trim();
  const baseSlug = slugifyCricketTeamName(raw);
  const slug = ALIAS_TO_SLUG[baseSlug] ?? baseSlug;
  let category: FavTeamCategory = "custom";
  if (IPL_SLUGS.has(slug)) category = "ipl";
  else if (NATIONAL_SLUGS.has(slug)) category = "national";
  return { slug, category, name: raw };
}

/** Canonical slug for a team name/slug (empty string when unresolvable). */
export function canonicalTeamSlug(value: string): string {
  return resolveCanonicalTeam(value).slug;
}

export function isIplSlug(slug: string): boolean {
  return IPL_SLUGS.has(slug);
}

export function isNationalSlug(slug: string): boolean {
  return NATIONAL_SLUGS.has(slug);
}
